import React, { useEffect, useState } from "react";
import { PrivateFileAnchor } from "../../../../utils/privateFile";
import http from "../../../../services/http";
import { lockOrUpdatePartnerPayoutLock } from "../../../../services/PartnerService";
import ChungNhanTram from "./ChungNhanTram";
import HoSoKyTram from "./HoSoKyTram";
import ChiDinhNguoiNhanTram from "./ChiDinhNguoiNhanTram";
import { TaiThayChiDinh, TaiThayGiayPhep } from "./LamHoTram";
import { NguoiUyQuyen, TramCungBenKy, TramCungBenKyDaDuyet, UyQuyenKy } from "./BenKyNhieuTram";

/**
 * Duyệt giấy phép kinh doanh của trạm — Quân 09/10/2026: "tải lên giấy phép thì phải được admin
 * duyệt … không có giấy phép thì không cho thanh toán". Trạm chỉ rút tiền được khi Approved.
 * Điểm thuộc chuỗi dùng giấy phép của chủ chuỗi (máy chủ tự tìm đúng hồ sơ).
 */
type NameCandidate = { field: string; label: string; value: string | null };
type Data = {
	phaiNop: boolean;
	files: string[];
	status: "none" | "pending" | "approved" | "rejected";
	note: string | null;
	review?: { reviewedAt?: string; submittedAt?: string; matchedField?: string | null } | null;
	/** 20/10/2026: 3 trường tên trên tài khoản trạm để đối chiếu với giấy phép. */
	nameCandidates?: NameCandidate[];
	representativeNameOnLicence?: string | null;
	/** 26/10/2026: tên doanh nghiệp trên tài khoản (chủ TK, tên pháp nhân) + tên doanh nghiệp đã duyệt. */
	businessNameCandidates?: NameCandidate[];
	businessNameOnLicence?: string | null;
	nameMatch?: boolean | null;
	/** 26/10/2026 (SOP-LEGAL-BEN-KY v1.0): dữ liệu tài khoản để đối chiếu 5 điều kiện. */
	stationAddress?: string | null;
	contractSigner?: string | null;
	bankAccountHolders?: string[];
	signingParty?: { id: number; legalName: string; status: string; kind?: string; registrationNo?: string | null } | null;
	verification?: XacMinh | null;
	/** 27/10/2026 (GĐ1 + GĐ2): trạm cùng bên ký, bên ký đã xác minh ở trạm khác, địa chỉ đã xác nhận của trạm này. */
	siblings?: TramCungBenKy[];
	entityVerifiedAt?: { stationId: number; verification: XacMinh } | null;
	addressReview?: { status: string; evidenceType?: string; evidenceRef?: string | null; fromPartnerId?: number } | null;
	/** 27/10/2026 (Đợt 1): gợi ý điền sẵn 5 ô + nguồn từng ô (licence_pdf · station_tax · signing_party · contract_signer). */
	prefill?: { values: Partial<XacMinh>; sources: Record<string, string> } | null;
};

/** Nhãn nguồn điền sẵn — Admin vẫn phải đối chiếu với giấy. */
const NGUON: Record<string, { t: string; c: string }> = {
	licence_pdf: { t: "read from the licence PDF", c: "#067647" },
	ai_read: { t: "read by AI from the licence — check against the file", c: "#6941C6" },
	station_tax: { t: "from the station's tax details — check against the licence", c: "#B54708" },
	signing_party: { t: "from the signing party on file — check against the licence", c: "#B54708" },
	contract_signer: { t: "from the station's contract signer — check against the licence", c: "#B54708" },
};
const NHAN_O: Record<string, string> = {
	kind: "Entity type",
	registeredName: "Registered name",
	registrationNo: "Registration no.",
	registeredAddress: "Address",
	representativeName: "Representative",
};
/** 27/10/2026: kết quả AI đọc giấy phép (backend services/partner/aiDocGiayPhep.js). */
type AiO = { value: string | null; evidence?: string | null; confidence?: number };
type AiDoc = { documentType?: string; issues?: string[]; fields?: Record<string, AiO>; model?: string; readAt?: string };
const LOI_AI: Record<string, string> = {
	blurry: "Photo is blurry — ask the station for a clearer photo.",
	cropped: "Part of the document is cut off.",
	not_a_licence: "This does not look like a business licence.",
	expired: "The licence looks expired.",
	multiple_documents: "Several documents in one file — check which one applies.",
	handwritten_changes: "Handwritten changes on the document — check carefully.",
	branch_document: "Branch or business-location certificate — ask for the parent company's registration certificate.",
};

/** So tên kiểu backend (bỏ dấu, hoa/thường, thứ tự) — chỉ để hiện cảnh báo sớm. */
const chuanTen = (v?: string | null) =>
	String(v || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[đĐ]/g, "d")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.trim()
		.split(/\s+/)
		.sort()
		.join(" ");

/** Phần xác minh admin nhập theo giấy phép — máy chủ kiểm lại (services/partner/xacMinhBenKy.js). */
type NguoiChiDinh = { name: string; documentRef: string };
type XacMinh = {
	kind: "" | "company" | "household" | "individual";
	registeredName: string;
	registrationNo: string;
	registeredAddress: string;
	representativeName: string;
	addressCovered: boolean;
	authorisedSigner?: NguoiChiDinh | null;
	authorisedPayee?: NguoiChiDinh | null;
	authorisedSigners?: NguoiUyQuyen[];
	signerNameAtApproval?: string | null;
	signerAuthority?: "representative" | "authorised_signer" | "self_declared" | null;
	legalEntityId?: number | null;
};
const XAC_MINH_TRONG: XacMinh = {
	kind: "",
	registeredName: "",
	registrationNo: "",
	registeredAddress: "",
	representativeName: "",
	addressCovered: false,
};
const LOAI_HINH: Array<{ id: XacMinh["kind"]; label: string; payout: string }> = [
	{ id: "company", label: "Company", payout: "account in the company's name" },
	{ id: "household", label: "Household business", payout: "account of the household owner or in the household's name" },
	{ id: "individual", label: "Individual", payout: "account of that person" },
];

/* 26/10/2026 (Quân): lý do từ chối soạn sẵn — Quân duyệt chữ ngắn. Gửi kèm mã (id) ⇒ trạm VN đọc bản Việt
   (backend services/partner/lyDoGiayPhep.js, cùng mã). Sửa chữ trong ô thì gửi như lý do tự gõ (không mã). */
export const LY_DO_MAU: Array<{ id: string; label: string; text: string }> = [
	{
		id: "name",
		label: "Names do not match",
		text: "Names on the licence don't match your account.",
	},
	{
		id: "blurry",
		label: "Photo not clear",
		text: "Photo unclear. Please upload a clear photo or PDF.",
	},
	{
		id: "wrong",
		label: "Not a business licence",
		text: "This is not a business licence.",
	},
	{
		id: "expired",
		label: "Licence expired",
		text: "The licence has expired.",
	},
	// 26/10/2026 (SOP-LEGAL-BEN-KY v1.0): điều kiện 4 (tiền về bên ký). 28/10 (SOP v1.1): bỏ lý do "địa chỉ".
	{
		id: "account",
		label: "Payout account not in licence name",
		text: "Your payout account must be in the name on the licence.",
	},
];

const MAU: Record<Data["status"], { bg: string; fg: string; label: string }> = {
	none: { bg: "#F2F4F7", fg: "#475467", label: "Not uploaded" },
	pending: { bg: "#FFFAEB", fg: "#B54708", label: "Waiting for review" },
	approved: { bg: "#ECFDF3", fg: "#067647", label: "Approved" },
	rejected: { bg: "#FEF3F2", fg: "#B42318", label: "Rejected" },
};

/** `onChanged` (26/10/2026): hàng chờ Business licences tải lại sau khi duyệt / từ chối. */
/** `formId` (26/10/2026): giấy phép nộp TRƯỚC KHI KÝ nằm trên đơn ⇒ cùng khối, đổi endpoint sang /admin/forms. */
export default function BusinessLicenceReview(props: {
	partnerId?: string;
	formId?: string;
	open: boolean;
	onChanged?: () => void;
	/** 28/10/2026: tài khoản chủ chuỗi thuần — không có địa chỉ trạm / tài khoản nhận tiền riêng. */
	taiKhoanChuoi?: boolean;
}) {
	const goc = props.formId ? `/admin/forms/${props.formId}` : `/admin/partners/${props.partnerId}`;
	const [d, setD] = useState<Data | null>(null);
	const [busy, setBusy] = useState(false);
	const [lyDo, setLyDo] = useState("");
	const [moTuChoi, setMoTuChoi] = useState(false);
	const [loi, setLoi] = useState("");
	// 28/10/2026 (Quân, bước 4): bật ký lại ⇒ duyệt vẫn qua dù tài khoản lệch; backend trả cảnh báo để Admin xử lý.
	const [canhBao, setCanhBao] = useState<{ code: string; message: string }[]>([]);
	const [daKhoa, setDaKhoa] = useState(false);
	// 30/10/2026: tải giấy chỉ định thay trạm xong ⇒ dựng lại danh sách chỉ định.
	const [khoaChiDinh, setKhoaChiDinh] = useState(0);
	// 26/10/2026 (Quân duyệt SOP-LEGAL-BEN-KY v1.0): nhập đủ thông tin trên giấy + tick địa chỉ mới duyệt được.
	const [xm, setXm] = useState<XacMinh>(XAC_MINH_TRONG);
	const [moNgoaiLe, setMoNgoaiLe] = useState(false);
	const [kyThay, setKyThay] = useState<NguoiChiDinh>({ name: "", documentRef: "" });
	const [nhanThay, setNhanThay] = useState<NguoiChiDinh>({ name: "", documentRef: "" });
	// 27/10/2026 (Đợt 1): ô đã điền sẵn + nguồn; Admin sửa ô nào thì bỏ nhãn nguồn của ô đó.
	const [nguon, setNguon] = useState<Record<string, string>>({});
	// 27/10/2026: AI đọc giấy phép — chỉ gợi ý; kết quả lưu sẵn ở review.aiRead.
	const [aiDoc, setAiDoc] = useState<AiDoc | null>(null);
	const [aiBusy, setAiBusy] = useState(false);
	const [aiLoi, setAiLoi] = useState("");
	/** Điền kết quả AI vào ô còn trống hoặc ô đang chỉ là gợi ý từ dữ liệu trạm (không đè chữ Admin gõ). */
	const apAi = (r: AiDoc | null) => {
		if (!r?.fields) return;
		setAiDoc(r);
		setXm((o) => {
			const m: any = { ...o };
			setNguon((n0) => {
				const n = { ...n0 };
				for (const [k, x] of Object.entries(r.fields || {})) {
					if (!x?.value) continue;
					if (!m[k] || (n[k] && n[k] !== "ai_read")) {
						m[k] = x.value;
						n[k] = "ai_read";
					}
				}
				return n;
			});
			return m;
		});
	};
	const docBangAi = async () => {
		setAiBusy(true);
		setAiLoi("");
		try {
			const { data } = await http.post(`${goc}/business-licence-ai-read`, { force: Boolean(aiDoc) });
			apAi(data?.data || null);
		} catch (e: any) {
			setAiLoi(e?.response?.data?.message || "AI could not read this licence. Fill in the fields manually.");
		} finally {
			setAiBusy(false);
		}
	};
	const dat = (k: keyof XacMinh, v: any) => {
		setXm((o) => ({ ...o, [k]: v }));
		setNguon((n) => {
			if (!n[k as string]) return n;
			const m = { ...n };
			delete m[k as string];
			return m;
		});
	};
	// 27/10/2026 (GĐ1): trạm cùng bên ký có địa chỉ trên giấy này; mở form đầy đủ dù bên ký đã xác minh ở trạm khác.
	const [tickTram, setTickTram] = useState<number[]>([]);
	const [dayDu, setDayDu] = useState(false);

	const tai = async () => {
		try {
			const { data } = await http.get(`${goc}/business-licence`);
			const dd: Data | null = data?.data || null;
			setD(dd);
			const goiY = dd?.prefill;
			if (goiY && dd?.status !== "approved") {
				// Chỉ điền ô còn trống — không đè chữ Admin đang gõ dở.
				setXm((o) => {
					const m: any = { ...o };
					const ns: Record<string, string> = {};
					for (const [k, v] of Object.entries(goiY.values || {})) {
						if (v && !m[k]) {
							m[k] = v;
							if (goiY.sources?.[k]) ns[k] = goiY.sources[k];
						}
					}
					setNguon(ns);
					return m;
				});
			}
			const daDoc = (dd?.review as any)?.aiRead;
			if (daDoc && dd?.status !== "approved") apAi(daDoc);
		} catch {
			setD(null);
		}
	};
	useEffect(() => {
		if (props.open && (props.partnerId || props.formId)) tai();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.open, props.partnerId, props.formId]);

	const duyet = async (status: "approved" | "rejected") => {
		if (status === "rejected" && !lyDo.trim()) {
			setLoi("Please write a reason so the station knows what to fix.");
			return;
		}
		if (status === "approved") {
			const thieu = !xm.kind || !xm.registeredName.trim() || !xm.registrationNo.trim() || !xm.registeredAddress.trim() || !xm.representativeName.trim();
			if (thieu) {
				setLoi("Fill in every field exactly as printed on the licence.");
				return;
			}
		}
		const coAi = (o: NguoiChiDinh) => (o.name.trim() || o.documentRef.trim() ? { name: o.name.trim(), documentRef: o.documentRef.trim() } : null);
		setBusy(true);
		setLoi("");
		try {
			const { data: kq } = await http.patch(`${goc}/business-licence-review`, {
				status,
				note: status === "rejected" ? lyDo.trim() : undefined,
				reasonCode: status === "rejected" ? LY_DO_MAU.find((m) => m.text === lyDo.trim())?.id : undefined,
				verification:
					status === "approved"
						? {
								kind: xm.kind,
								registeredName: xm.registeredName.trim(),
								registrationNo: xm.registrationNo.trim(),
								registeredAddress: xm.registeredAddress.trim(),
								representativeName: xm.representativeName.trim(),
								addressCovered: xm.addressCovered,
								authorisedSigner: coAi(kyThay),
								authorisedPayee: coAi(nhanThay),
								coveredStationIds: tickTram,
						  }
						: undefined,
			});
			const ds = kq?.data?.warnings ?? kq?.warnings;
			setCanhBao(status === "approved" && Array.isArray(ds) ? ds : []);
			setDaKhoa(false);
			setMoTuChoi(false);
			setLyDo("");
			setXm(XAC_MINH_TRONG);
			setKyThay({ name: "", documentRef: "" });
			setNhanThay({ name: "", documentRef: "" });
			setTickTram([]);
			await tai();
			props.onChanged?.();
		} catch (e: any) {
			setLoi(e?.response?.data?.message || "Could not save. Please try again.");
		} finally {
			setBusy(false);
		}
	};

	if (!d) return null;
	const m = MAU[d.status] || MAU.none;
	const o: React.CSSProperties = { padding: "7px 10px", border: "1px solid #D0D5DD", borderRadius: 8, fontSize: 13, flex: "1 1 180px" };
	const nhanO = (flex: string): React.CSSProperties => ({ display: "grid", gap: 3, flex, minWidth: 0 });
	const nhanChu: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: "#475467" };
	const nut = (bg: string): React.CSSProperties => ({
		border: "none",
		borderRadius: 8,
		padding: "7px 14px",
		background: bg,
		color: "#fff",
		fontWeight: 700,
		fontSize: 13,
		cursor: busy ? "wait" : "pointer",
		opacity: busy ? 0.6 : 1,
	});

	return (
		<div
			data-testid="business-licence-review"
			style={{ border: "1px solid #D0D5DD", borderRadius: 10, padding: "14px 16px", marginBottom: 16, background: "#fff" }}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
				<div style={{ fontWeight: 700, fontSize: 14, color: "#101828" }}>Business licence</div>
				<span style={{ background: m.bg, color: m.fg, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
					{m.label}
				</span>
			</div>
			<p style={{ margin: "0 0 10px", fontSize: 12.5, color: "#475467", lineHeight: 1.45 }}>
				{d.phaiNop
					? "Required for every station: payouts stay blocked until the licence is Approved."
					: "Licence check is switched off on this server. Reviewing is optional."}
				{d.status === "rejected" && d.note ? ` Last reason: "${d.note}".` : ""}
			</p>
			{/* 26/10/2026 (SOP-LEGAL-BEN-KY v1.0): 5 điều kiện + dữ liệu tài khoản để đối chiếu. */}
			<div
				data-testid="licence-approve-condition"
				style={{
					margin: "0 0 10px",
					padding: "8px 10px",
					background: "#F9FAFB",
					border: "1px solid #EAECF0",
					borderRadius: 8,
					fontSize: 12.5,
					color: "#344054",
					lineHeight: 1.5,
				}}>
				<strong>Approve only if all 4 hold</strong> (internal SOP <code>SOP-LEGAL-BEN-KY</code>):
				<ol style={{ margin: "4px 0 6px", paddingLeft: 18 }}>
					<li>Entity — type, registered name and number typed exactly as on the licence.</li>
					<li>Signer — the contract signer is the owner / legal representative on the licence, or confirms their authority when signing (authority letter only for multi-entity chains).</li>
					<li>Payout — company ⇒ account in the company&apos;s name; household ⇒ owner or household name; individual ⇒ that person. Otherwise a written payee instruction.</li>
					<li>Signing evidence — recorded automatically when the station signs.</li>
				</ol>
				<table style={{ borderCollapse: "collapse", fontSize: 12.5 }}>
					<tbody>
						{(
							[
								...(props.taiKhoanChuoi
									? []
									: [[props.formId ? "Station address (application)" : "Station address", d.stationAddress]]),
								[props.formId ? "Contract signer (application)" : "Contract signer", d.contractSigner],
								...(props.taiKhoanChuoi
									? []
									: [
											[
												"Payout account holder",
												props.formId ? "— (no bank account before signing)" : (d.bankAccountHolders || []).join(" · "),
											],
									  ]),
								...(props.formId
									? []
									: [
											[
												"Signing party on file",
												d.signingParty ? `${d.signingParty.legalName} (#${d.signingParty.id}, ${d.signingParty.status})` : "",
											],
									  ]),
							] as Array<[string, string | null | undefined]>
						).map(([k, v]) => (
							<tr key={k}>
								<td style={{ padding: "1px 12px 1px 0", color: "#667085", verticalAlign: "top" }}>{k}</td>
								<td style={{ padding: "1px 0", fontWeight: 600, wordBreak: "break-word", overflowWrap: "anywhere" }}>
									{v || <span style={{ color: "#98A2B3", fontWeight: 400 }}>(empty)</span>}
								</td>
							</tr>
						))}
					</tbody>
				</table>
				{d.status === "approved" && d.verification ? (
					<div data-testid="licence-verification-summary" style={{ marginTop: 6, color: "#067647" }}>
						✓ Verified as {LOAI_HINH.find((l) => l.id === d.verification?.kind)?.label || d.verification.kind}:{" "}
						<strong>{d.verification.registeredName}</strong> (no. {d.verification.registrationNo}) ·{" "}
						{d.verification.registeredAddress} · representative {d.verification.representativeName}
						{d.verification.authorisedPayee ? ` · authorised payee ${d.verification.authorisedPayee.name} (${d.verification.authorisedPayee.documentRef})` : ""}
						{d.verification.signerAuthority === "self_declared" ? (
							<div data-testid="licence-signer-self-declared" style={{ color: "#B54708", marginTop: 4 }}>
								⚠ Signer {d.verification.signerNameAtApproval || ""} differs from the licence representative — approved on
								the signer&apos;s declaration of authority.
							</div>
						) : null}
					</div>
				) : d.status === "approved" ? (
					<div style={{ marginTop: 6, color: "#B54708" }}>
						Approved before the 5-condition rule
						{d.representativeNameOnLicence || d.businessNameOnLicence
							? ` (name on licence: ${[d.representativeNameOnLicence, d.businessNameOnLicence].filter(Boolean).join(" / ")})`
							: ""}
						. Revoke and approve again to record the full check.
						{d.nameMatch === false ? <span style={{ color: "#B42318" }}> Name no longer matches: payouts blocked.</span> : null}
					</div>
				) : null}
				{!props.formId && d.status === "approved" && d.verification ? (
					<>
						<UyQuyenKy
							partnerId={Number(props.partnerId)}
							ds={[
								...(d.verification.authorisedSigners || []),
								...(d.verification.authorisedSigner &&
								!(d.verification.authorisedSigners || []).some((x) => x.name === d.verification?.authorisedSigner?.name)
									? [d.verification.authorisedSigner]
									: []),
							]}
							onDone={tai}
						/>
						<TramCungBenKyDaDuyet siblings={d.siblings || []} onDone={tai} />
					</>
				) : null}
				{!props.formId && props.taiKhoanChuoi && d.entityVerifiedAt && !dayDu ? (
					<div data-testid="licence-chain-account" style={{ marginTop: 10, padding: "8px 10px", background: "#EFF8FF", borderRadius: 8 }}>
						<strong>Signing party already verified</strong> on station #{d.entityVerifiedAt.stationId}:{" "}
						{d.entityVerifiedAt.verification.registeredName}. Every station of this signing party is covered.
					</div>
				) : null}
				{!props.formId && !props.taiKhoanChuoi && d.entityVerifiedAt && !dayDu ? (
					<div data-testid="licence-address-only" style={{ marginTop: 10, padding: "8px 10px", background: "#EFF8FF", borderRadius: 8 }}>
						<div>
							<strong>Signing party already verified</strong> on station #{d.entityVerifiedAt.stationId}:{" "}
							{d.entityVerifiedAt.verification.registeredName} (no. {d.entityVerifiedAt.verification.registrationNo}) ·
							representative {d.entityVerifiedAt.verification.representativeName}. This station is covered by that
							licence — no address check needed (SOP v1.1). Payouts still go only to an account in the signing
							party&apos;s name or a designated account.
						</div>
						{d.addressReview?.status === "approved" ? (
							<div style={{ color: "#067647", fontWeight: 600, margin: "4px 0" }}>
								✓ Address verified{d.addressReview.evidenceRef ? ` (${d.addressReview.evidenceRef})` : ""}
							</div>
						) : null}
						<button
							type="button"
							onClick={() => setDayDu(true)}
							style={{ marginTop: 6, border: "none", background: "none", padding: 0, color: "#0F5F9E", fontSize: 12, cursor: "pointer" }}>
							This licence is a different business — use the full review
						</button>
					</div>
				) : null}
			</div>
			{d.files.length ? (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
					{d.files.map((f, i) => (
						<PrivateFileAnchor
							key={f}
							path={f}
							style={{ fontSize: 13, color: "#0F5F9E", fontWeight: 600 }}>
							📄 File {i + 1}
						</PrivateFileAnchor>
					))}
				</div>
			) : null}
			{d.files.length && d.status !== "approved" && (!d.entityVerifiedAt || dayDu || props.formId) ? (
				<div data-testid="licence-verification-form" style={{ display: "grid", gap: 8, marginBottom: 10 }}>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
						{/* 27/10/2026 (Quân): tiêu đề cố định trên từng ô — chữ gợi ý biến mất khi ô đã có chữ nên không biết ô nào là gì. */}
						<label style={nhanO("1 1 180px")}>
							<span style={nhanChu}>Entity type</span>
						<select
							data-testid="licence-kind"
							value={xm.kind}
							onChange={(e) => dat("kind", e.target.value)}
							style={{ ...o, flex: "none", width: "100%" }}>
							<option value="">Entity type on licence…</option>
							{LOAI_HINH.map((l) => (
								<option key={l.id} value={l.id}>
									{l.label}
								</option>
							))}
						</select>
						</label>
						<label style={nhanO("2 1 260px")}>
							<span style={nhanChu}>Registered name (business / household name)</span>
							<input data-testid="licence-registered-name" value={xm.registeredName} onChange={(e) => dat("registeredName", e.target.value)} placeholder="Registered name (company / household / person)" style={{ ...o, flex: "none", width: "100%" }} />
						</label>
						<label style={nhanO("1 1 160px")}>
							<span style={nhanChu}>Registration / tax no.</span>
							<input data-testid="licence-registration-no" value={xm.registrationNo} onChange={(e) => dat("registrationNo", e.target.value)} placeholder="Registration / tax number" style={{ ...o, flex: "none", width: "100%" }} />
						</label>
					</div>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
						<label style={nhanO("2 1 300px")}>
							<span style={nhanChu}>Address on the licence</span>
							<input data-testid="licence-registered-address" value={xm.registeredAddress} onChange={(e) => dat("registeredAddress", e.target.value)} placeholder="Address on the licence" style={{ ...o, flex: "none", width: "100%" }} />
						</label>
						<label style={nhanO("1 1 200px")}>
							<span style={nhanChu}>Owner / legal representative (person)</span>
							<input data-testid="licence-representative-name" value={xm.representativeName} onChange={(e) => dat("representativeName", e.target.value)} placeholder="Owner / legal representative" style={{ ...o, flex: "none", width: "100%" }} />
						</label>
					</div>
					{!props.formId ? (
						<div data-testid="licence-ai" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 12.5 }}>
							<button
								type="button"
								disabled={aiBusy}
								onClick={docBangAi}
								style={{ border: "1px solid #D6BBFB", background: "#F9F5FF", color: "#6941C6", borderRadius: 8, padding: "6px 12px", fontWeight: 700, cursor: aiBusy ? "wait" : "pointer" }}>
								{aiBusy ? "Reading…" : aiDoc ? "✨ Read again with AI" : "✨ Read with AI"}
							</button>
							<span style={{ color: "#667085" }}>AI fills the fields from the licence file. You still check every field and the address.</span>
							{aiLoi ? <span style={{ color: "#B42318", flexBasis: "100%" }}>{aiLoi}</span> : null}
							{(aiDoc?.issues || []).map((i) => (
								<span key={i} style={{ color: "#B42318", fontWeight: 600, flexBasis: "100%" }}>
									⚠ {LOI_AI[i] || i}
								</span>
							))}
						</div>
					) : null}
					{Object.keys(nguon).length ? (
						<div data-testid="licence-prefill-sources" style={{ fontSize: 12, lineHeight: 1.5 }}>
							<strong style={{ color: "#344054" }}>Autofilled — check each field against the licence:</strong>
							{Object.entries(nguon).map(([k, n]) => (
								<div key={k} style={{ color: NGUON[n]?.c || "#667085" }}>
									{NHAN_O[k] || k}: {NGUON[n]?.t || n}
									{n === "ai_read" && aiDoc?.fields?.[k]?.evidence ? (
										<span style={{ color: "#667085" }}> — “{aiDoc.fields[k].evidence}”</span>
									) : null}
								</div>
							))}
						</div>
					) : null}
					{xm.representativeName.trim() && d.contractSigner && chuanTen(xm.representativeName) !== chuanTen(d.contractSigner) ? (
						<div data-testid="licence-signer-warning" style={{ fontSize: 12.5, color: "#B54708" }}>
							⚠ Signer &quot;{d.contractSigner}&quot; differs from the licence representative. Approval relies on the signer&apos;s
							declaration of authority (multi-entity chains: authority letter required under Exceptions).
						</div>
					) : null}
					{xm.kind ? (
						<div style={{ fontSize: 12, color: "#667085" }}>
							Payouts will only go to the {LOAI_HINH.find((l) => l.id === xm.kind)?.payout}.
						</div>
					) : null}
					<label style={{ display: "flex", gap: 6, alignItems: "flex-start", fontSize: 13, color: "#101828" }}>
						<input data-testid="licence-address-covered" type="checkbox" checked={xm.addressCovered} onChange={(e) => dat("addressCovered", e.target.checked)} style={{ marginTop: 3 }} />
						<span>
							{props.taiKhoanChuoi
								? "Chain owner account — station address is on this licence (for the record only)"
								: "Station address is on this licence (for the record only — not required to approve)"}
						</span>
					</label>
					{/* 28/10/2026 (SOP v1.1): bỏ tick "địa chỉ trạm cùng bên ký có trên giấy" — trạm cùng bên ký tự được tính. */}
					<button
						type="button"
						onClick={() => setMoNgoaiLe((v) => !v)}
						style={{ justifySelf: "start", border: "none", background: "none", padding: 0, color: "#0F5F9E", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
						{moNgoaiLe ? "▾" : "▸"} Exceptions (only with a signed document)
					</button>
					{moNgoaiLe ? (
						<div style={{ display: "grid", gap: 6 }}>
							<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
								<input data-testid="licence-signer-name" value={kyThay.name} onChange={(e) => setKyThay({ ...kyThay, name: e.target.value })} placeholder="Authorised signer (if not the representative)" style={{ ...o, flex: "1 1 220px" }} />
								<input data-testid="licence-signer-ref" value={kyThay.documentRef} onChange={(e) => setKyThay({ ...kyThay, documentRef: e.target.value })} placeholder="Authority letter ref (multi-entity chains only)" style={{ ...o, flex: "1 1 180px" }} />
							</div>
							{/* 02/11/2026 (Quân): bỏ 2 ô "Authorised payee" — trùng luồng "Payout account designations" (30/10).
							    Chỉ ẩn ô; dữ liệu cũ + luật so tên ở backend giữ nguyên (prod 02/11: không trạm nào dùng). */}
							<div data-testid="licence-payee-hint" style={{ fontSize: 12.5, color: "#475467" }}>
								Other account holder? Use Payout account designations on the station.
							</div>
						</div>
					) : null}
				</div>
			) : null}
			{d.files.length && d.status !== "approved" ? (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
					{/* 27/10/2026: bên ký đã xác minh ở trạm khác ⇒ duyệt ở khung "address only" phía trên, nút này ẩn. */}
					{!d.entityVerifiedAt || dayDu || props.formId ? (
						<button type="button" disabled={busy} style={nut("#067647")} onClick={() => duyet("approved")}>
							Approve
						</button>
					) : null}
					{!moTuChoi ? (
						<button type="button" disabled={busy} style={nut("#B42318")} onClick={() => setMoTuChoi(true)}>
							Reject…
						</button>
					) : (
						<>
							<select
								data-testid="licence-reject-template"
								value=""
								onChange={(e) => {
									const m = LY_DO_MAU.find((x) => x.id === e.target.value);
									if (m) setLyDo(m.text);
								}}
								style={{ padding: "7px 8px", border: "1px solid #D0D5DD", borderRadius: 8, fontSize: 13 }}>
								<option value="">Pick a reason…</option>
								{LY_DO_MAU.map((m) => (
									<option key={m.id} value={m.id}>
										{m.label}
									</option>
								))}
							</select>
							<input
								data-testid="licence-reject-reason"
								value={lyDo}
								onChange={(e) => setLyDo(e.target.value)}
								placeholder="Reason shown to the station (e.g. photo is blurry)"
								style={{ flex: "1 1 260px", padding: "7px 10px", border: "1px solid #D0D5DD", borderRadius: 8, fontSize: 13 }}
							/>
							<button type="button" disabled={busy} style={nut("#B42318")} onClick={() => duyet("rejected")}>
								Reject
							</button>
						</>
					)}
				</div>
			) : null}
			{d.status === "approved" && d.files.length ? (
				<button type="button" disabled={busy} style={{ ...nut("#98A2B3"), marginTop: 2 }} onClick={() => setMoTuChoi(true)}>
					Revoke approval…
				</button>
			) : null}
			{d.status === "approved" && moTuChoi ? (
				<div style={{ display: "flex", gap: 8, marginTop: 8 }}>
					<input
						value={lyDo}
						onChange={(e) => setLyDo(e.target.value)}
						placeholder="Reason shown to the station"
						style={{ flex: 1, padding: "7px 10px", border: "1px solid #D0D5DD", borderRadius: 8, fontSize: 13 }}
					/>
					<button type="button" disabled={busy} style={nut("#B42318")} onClick={() => duyet("rejected")}>
						Reject
					</button>
				</div>
			) : null}
			{loi ? <p style={{ margin: "8px 0 0", color: "#B42318", fontSize: 12.5 }}>{loi}</p> : null}
			{canhBao.length ? (
				<div
					data-testid="licence-approve-warnings"
					style={{ marginTop: 10, padding: "10px 12px", border: "1px solid #FEC84B", background: "#FFFAEB", borderRadius: 8, fontSize: 12.5, color: "#7A2E0E" }}>
					<strong>Approved — check before payout</strong>
					<ul style={{ margin: "6px 0 8px", paddingLeft: 18 }}>
						{canhBao.map((c) => (
							<li key={c.code}>{c.message}</li>
						))}
					</ul>
					{canhBao.some((c) => c.code === "BANK_HOLDER_NOT_SIGNING_PARTY") && props.partnerId && !props.formId ? (
						daKhoa ? (
							<span data-testid="licence-account-locked">Payouts locked. The station sees the reason and must change the payout account or designate it.</span>
						) : (
							<button
								type="button"
								data-testid="licence-mark-account-invalid"
								disabled={busy}
								style={nut("#B54708")}
								onClick={async () => {
									setBusy(true);
									try {
										await lockOrUpdatePartnerPayoutLock(
											String(props.partnerId),
											"Payout account is not in the name on your business licence. Please change the payout account, or designate this account as an authorised payee."
										);
										setDaKhoa(true);
										props.onChanged?.();
									} catch (e: any) {
										setLoi(e?.response?.data?.message || "Could not lock payouts. Please try again.");
									} finally {
										setBusy(false);
									}
								}}>
								Mark payout account invalid (lock payouts)
							</button>
						)
					) : null}
				</div>
			) : null}
			{!props.formId && props.partnerId ? <HoSoKyTram partnerId={props.partnerId} onChanged={props.onChanged} /> : null}
			{!props.formId && props.partnerId ? <ChungNhanTram partnerId={props.partnerId} /> : null}
			{!props.formId && props.partnerId ? (
				<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
					<TaiThayGiayPhep
						partnerId={props.partnerId}
						onDone={() => {
							tai();
							props.onChanged?.();
						}}
					/>
					{!props.taiKhoanChuoi ? (
						<TaiThayChiDinh
							partnerId={props.partnerId}
							onDone={() => {
								setKhoaChiDinh((k) => k + 1);
								props.onChanged?.();
							}}
						/>
					) : null}
				</div>
			) : null}
			{!props.formId && props.partnerId ? (
				<ChiDinhNguoiNhanTram
					key={khoaChiDinh}
					partnerId={props.partnerId}
					onChanged={props.onChanged}
				/>
			) : null}
		</div>
	);
}
